import isNull from 'lodash/isNull';
import { FormattedMessage } from 'react-intl';

import { get } from 'utils/lodash';
import { removeStringComma } from 'utils/formatter';
import { formatFromAccountListPayload } from 'utils/transaction';

import { CURRENCY_IDR, DEFAULT_INT_ZERO } from 'settings/constants/common';
import { DEFAULT_MAX_TRANSFER_AMOUNT, DEFAULT_MIN_TRANSFER_AMOUNT } from 'settings/constants/transaction';
import { getVirtualAccountDetail, getVAFavouriteDetail } from 'middleware/actions/transfer';

export const getVirtualConfirmationBody = payload => {
  const favTransaction = get(payload, 'favouriteTransaction', false);
  const initialSenderAccounts = get(payload, 'initialSenderAccounts', '');
  const accountLimit = get(payload, 'accountLimit', '') || get(payload, 'transferDetail.accountLimit', '');

  return {
    userId: get(payload, 'userId', ''),
    toAccountNumber: get(payload, 'accountNumber', ''),
    fromAccountNumber: get(payload, 'senderAccount.accountNumber', ''),
    fromCurrency: get(payload, 'senderAccount.currency', ''),
    toAmount: removeStringComma(get(payload, 'transferAmount', '')),
    fromAccountList: formatFromAccountListPayload(initialSenderAccounts),
    ...(favTransaction ? { favTransaction, accountLimit } : {}),
  };
};

export const getVirtualAccountDynamicFields = (detail, senderAccount, isFavouriteTransfer) => {
  const currentBalance = get(senderAccount, 'plainBalance', DEFAULT_INT_ZERO);
  const currency = get(senderAccount, 'currency', CURRENCY_IDR);

  return [
    {
      name: 'accountNumber',
      readOnly: isFavouriteTransfer,
    },
    {
      name: 'transferAmount',
      validationRules: [
        { type: 'required', isValidateRequired: true },
        {
          type: 'senderBalance',
          value: currentBalance,
          isValidateRequired: currency === CURRENCY_IDR,
        },
        { type: 'minAmount', value: DEFAULT_MIN_TRANSFER_AMOUNT, isValidateRequired: true },
        { type: 'maxAmount', value: DEFAULT_MAX_TRANSFER_AMOUNT, isValidateRequired: true },
      ],
    },
  ];
};

export const getVirtualAccountMonetaries = detail => {
  const transactionAmount = get(detail, 'toAmountDis', '');
  const transactionFee = get(detail, 'chargeAmountDis', '');
  const debitedAmount = get(detail, 'debitAmountDis', '');
  const localDebitedAmount = get(detail, 'localAmountDis', '');

  return [
    { title: <FormattedMessage id="transaction.transfer.transactionAmount" />, info: transactionAmount },
    { title: <FormattedMessage id="transaction.transfer.transactionFee" />, info: transactionFee },
    { title: <FormattedMessage id="transaction.transfer.totalDebit" />, info: debitedAmount },
    { title: <FormattedMessage id="transaction.transfer.localDebitedAmount" />, info: localDebitedAmount },
  ];
};

export const getVirtualAccountFavouriteDynamicFields = () => {
  return [
    {
      name: 'toAccountNo',
      readOnly: true,
    },
    {
      isHidden: false,
      name: 'accountHolderName',
    },
  ];
};

export const getVirtualAccountFavouriteDetail = (detail, isEditFavourite) => {
  const toAccountNo = get(detail, 'toAccountNo', '');
  const accountHolderName = get(detail, 'accountHolderName', '');
  const accountNickname = get(detail, 'accountNickname', '');
  const accountLimit = get(detail, 'accountLimit', '');
  const emailAddress = get(detail, 'emailAddress', '');

  return { toAccountNo, accountHolderName, ...(isEditFavourite && { accountNickname, emailAddress, accountLimit }) };
};

export const getVirtualAccountFavouriteBeneficiaryDetails = detail => [
  {
    title: <FormattedMessage id="transaction.favourite.form.beneficiaryNickname" />,
    info: get(detail, 'accountNickname', '-'),
  },
  {
    title: <FormattedMessage id="transaction.transfer.beneficiary.email" />,
    info: get(detail, 'emailAddress', '-'),
  },
];

export const getVirtualAccountEditFavouriteResultPayload = detail => ({
  userId: get(detail, 'userId', ''),
  tacValue: get(detail, 'tacValue', ''),
  restSMSService: get(detail, 'tacDetail.tacViewBean.restSMSService', {}),
  accountNickname: get(detail, 'confirmationDetail.accountNickname', ''),
  toAccountNo: get(detail, 'confirmationDetail.toAccountNo', ''),
  accountLimit: get(detail, 'confirmationDetail.accountLimit', ''),
  emailAddress: get(detail, 'confirmationDetail.emailAddress', ''),
});

export const getVirtualAccountDeleteFavouriteResultPayload = detail => ({
  userId: get(detail, 'userId', ''),
  tacValue: get(detail, 'tacValue', ''),
  restSMSService: get(detail, 'tacDetail.tacViewBean.restSMSService', {}),
  toAccountNo: get(detail, 'confirmationDetail.toAccountNo', ''),
});

export const getVirtualAccountDetailDispatcher = ({ userId, favouriteIndex, favouriteAccount, isSenderIDR, dispatch }) => {
  const favAccountMaps = get(favouriteAccount, 'favouriteAccountMap', {});

  if (!isSenderIDR) return getVirtualAccountDetail();

  return !isNull(favouriteIndex)
    ? dispatch(getVAFavouriteDetail({ userId, favIndex: favouriteIndex, favAccountMaps }))
    : dispatch(getVirtualAccountDetail({ userId }));
};
